import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConversationHistoryService } from './conversation-history.service';
import {
  LiveTranslationSegment,
  LiveTranslationService,
  LiveTranslationSession,
} from './live-translation.service';
import { TranslationService } from './translation.service';
import { TranslatorService } from './translator.service';
import { WordUsageService } from './word-usage.service';

export interface ContinuousTranscript {
  text: string;
  timestamp: Date;
  chunkIndex: number;
  originalText?: string; // Original text in source language (for conversation context)
}

@Injectable({
  providedIn: 'root',
})
export class ContinuousConversationService {
  // Pause-based chunking parameters
  private readonly MIN_CHUNK_DURATION_MS = 1000; // Minimum 1 second of speech before creating chunk
  private readonly MAX_CHUNK_DURATION_MS = 10000; // Maximum 10 seconds - force chunk even if no pause
  private readonly PAUSE_DURATION_MS = 800; // 800ms of silence = pause detected
  private readonly VAD_INTERVAL_MS = 100; // Check audio energy every 100ms

  // VAD thresholds (platform-specific)
  private readonly VAD_RMS_THRESHOLD = 0.01; // RMS below this = silence
  private readonly VAD_PEAK_THRESHOLD = 0.05; // Peak below this = silence

  private readonly transcripts$ = new BehaviorSubject<ContinuousTranscript[]>(
    []
  );
  private readonly isRecording$ = new BehaviorSubject<boolean>(false);
  private readonly error$ = new BehaviorSubject<string | null>(null);
  // Observable for audio chunks (for broadcast feature)
  private readonly audioChunkEvents$ = new BehaviorSubject<{
    blob: Blob;
    chunkIndex: number;
  } | null>(null);

  private mediaRecorder?: MediaRecorder;
  private audioChunks: Blob[] = [];
  private chunkIndex = 0;
  private sourceLanguage?: string;
  private targetLanguage = 'en';
  private systemPrompt?: string; // Store system prompt/topic
  private stream?: MediaStream;
  private pendingRequests = 0;
  private readonly MAX_CONCURRENT_REQUESTS = 2; // Limit concurrent transcription requests
  private readonly MAX_TRANSCRIPTS = 50; // Limit transcript history to prevent memory issues
  private audioContexts: AudioContext[] = []; // Track AudioContexts for cleanup

  // Duplicate detection to filter repeated messages during silence
  private recentTranscripts = new Map<string, number>(); // Map of normalized text -> timestamp
  private readonly DUPLICATE_WINDOW_MS = 10000; // 10 seconds - ignore same text within this window

  // Retry queue for failed chunks (timeout errors)
  private retryQueue: Array<{
    audioBlob: Blob;
    chunkIndex: number;
    retryCount: number;
    timestamp: number;
  }> = [];
  private readonly MAX_RETRIES = 2; // Maximum retry attempts per chunk
  private readonly RETRY_DELAY_MS = 2000; // Initial retry delay (2 seconds)
  private retryTimeout?: ReturnType<typeof setTimeout>;

  // VAD state for pause detection
  private vadAudioContext?: AudioContext;
  private vadAnalyser?: AnalyserNode;
  private vadSource?: MediaStreamAudioSourceNode;
  private vadCheckInterval?: ReturnType<typeof setInterval>;
  private maxChunkTimeout?: ReturnType<typeof setTimeout>;
  private lastSpeechTime = 0; // Timestamp of last detected speech
  private chunkStartTime = 0; // Timestamp when current chunk started
  private isInSpeech = false; // Whether we're currently detecting speech

  // Lecture mode: fixed-interval chunking (no VAD)
  private lectureMode = false;
  private useAdvancedModel = false;
  private lectureModeTimer?: ReturnType<typeof setTimeout>;
  private readonly LECTURE_MODE_INTERVAL_MS = 20000; // 20 seconds

  constructor(
    private readonly translatorService: TranslatorService,
    private readonly conversationHistoryService: ConversationHistoryService,
    private readonly translationService: TranslationService,
    private readonly wordUsageService: WordUsageService,
    private readonly liveTranslation: LiveTranslationService
  ) {}

  private liveSession?: LiveTranslationSession;
  private readonly liveCaption$ = new BehaviorSubject<string>('');

  get transcripts(): Observable<ContinuousTranscript[]> {
    return this.transcripts$.asObservable();
  }

  /** The sentence currently being live-translated (empty when none). */
  get liveCaption(): Observable<string> {
    return this.liveCaption$.asObservable();
  }

  isLiveRecording(): boolean {
    return !!this.liveSession;
  }

  setLiveAudioEnabled(enabled: boolean): void {
    this.liveSession?.setAudioEnabled(enabled);
  }

  /**
   * Continuous conversation through gpt-realtime-translate: speech is streamed
   * and translated while the speaker talks, with the translated voice played live.
   */
  async startLiveRecording(
    sourceLanguage: string,
    targetLanguage: string,
    playAudio: boolean
  ): Promise<void> {
    if (this.isRecording$.value) {
      return;
    }
    this.sourceLanguage = sourceLanguage;
    this.targetLanguage = targetLanguage;
    this.error$.next(null);

    this.liveSession = await this.liveTranslation.start({
      sourceLanguage,
      targetLanguage,
      playTranslatedAudio: playAudio,
      reportUsage: true,
      onPartial: (text) => this.liveCaption$.next(text),
      onSegment: (segment) => this.addLiveTranscript(segment),
      onError: (error, fatal) => {
        console.error('[ContinuousConversation] Live translation error:', error);
        if (fatal) {
          this.error$.next(error.message);
          this.stopRecording();
        }
      },
    });
    this.isRecording$.next(true);
  }

  private addLiveTranscript(segment: LiveTranslationSegment): void {
    const transcripts = this.transcripts$.value;
    transcripts.push({
      text: segment.translatedText,
      timestamp: new Date(),
      chunkIndex: this.chunkIndex++,
      originalText: segment.sourceText,
    });
    if (transcripts.length > this.MAX_TRANSCRIPTS) {
      transcripts.shift();
    }
    this.transcripts$.next([...transcripts]);

    this.conversationHistoryService.addConversation({
      originalText: segment.sourceText || segment.translatedText,
      translatedText: segment.translatedText,
      userLanguage: this.sourceLanguage || 'en',
      targetLanguage: this.targetLanguage,
      confidence: 1,
      inputType: 'speech',
    });
  }

  get isRecording(): Observable<boolean> {
    return this.isRecording$.asObservable();
  }

  get error(): Observable<string | null> {
    return this.error$.asObservable();
  }

  /**
   * Get observable for audio chunks (for broadcast feature)
   * Emits audio blobs when chunks are ready to be sent
   */
  get audioChunkEvents(): Observable<{
    blob: Blob;
    chunkIndex: number;
  } | null> {
    return this.audioChunkEvents$.asObservable();
  }

  async startRecording(
    sourceLanguage?: string,
    targetLanguage = 'en',
    systemPrompt?: string, // Add systemPrompt parameter
    lectureMode = false, // Optional lecture mode flag
    useAdvancedModel = false
  ): Promise<void> {
    if (this.isRecording$.value) {
      return;
    }

    // Ensure sourceLanguage is set - default to 'en' if not provided
    this.sourceLanguage = sourceLanguage || 'en';
    this.targetLanguage = targetLanguage || 'en';
    this.systemPrompt = systemPrompt; // Store system prompt
    this.lectureMode = lectureMode; // Store lecture mode preference
    this.useAdvancedModel = useAdvancedModel;

    // Clear duplicate tracking when starting a new recording session
    this.recentTranscripts.clear();

    console.log('[ContinuousConversation] Starting recording with languages:', {
      sourceLanguage: this.sourceLanguage,
      targetLanguage: this.targetLanguage,
      hasSystemPrompt: !!this.systemPrompt,
      systemPromptLength: this.systemPrompt?.length || 0,
    });
    // Don't reset chunkIndex here to avoid collisions with existing transcripts
    // this.chunkIndex = 0;
    this.audioChunks = [];
    this.error$.next(null);

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
      });

      // Use WebM for better mobile compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('[ContinuousConversation] MediaRecorder error', event);
        this.error$.next('Recording error occurred');
      };

      this.mediaRecorder.onstop = () => {
        // When recording stops, process the accumulated chunks
        if (this.audioChunks.length > 0 && this.isRecording$.value) {
          this.processChunk();
        }

        // Lecture mode: immediately restart to avoid gaps
        if (
          this.lectureMode &&
          this.isRecording$.value &&
          this.mediaRecorder &&
          this.mediaRecorder.state === 'inactive' &&
          this.stream &&
          this.stream.active
        ) {
          setTimeout(() => {
            if (
              this.isRecording$.value &&
              this.mediaRecorder &&
              this.mediaRecorder.state === 'inactive'
            ) {
              try {
                this.mediaRecorder.start();
                this.chunkStartTime = Date.now();
                this.startLectureModeTimer();
                console.log(
                  '[ContinuousConversation] Lecture mode: recording restarted (no gap)'
                );
              } catch (err) {
                console.warn(
                  '[ContinuousConversation] Lecture mode: failed to restart recorder',
                  err
                );
              }
            }
          }, 100);
        }
      };

      // Start recording
      this.mediaRecorder.start();
      this.isRecording$.next(true);

      this.chunkStartTime = Date.now();
      if (this.lectureMode) {
        // Lecture mode: use fixed 20-second interval
        this.startLectureModeTimer();
      } else {
        // Normal mode: use VAD for pause-based chunking
        this.initializeVAD();
        // Start chunk timer (max duration fallback)
        this.scheduleMaxChunkTimeout();
      }
    } catch (err) {
      console.error('[ContinuousConversation] Failed to start', err);
      this.error$.next(
        err instanceof Error ? err.message : 'Failed to access microphone'
      );
      throw err;
    }
  }

  stopRecording(): void {
    if (this.liveSession) {
      const session = this.liveSession;
      this.liveSession = undefined;
      // Sentences still being translated keep arriving while the session drains.
      session.stop().finally(() => this.liveCaption$.next(''));
      this.isRecording$.next(false);
      return;
    }
    if (!this.isRecording$.value) {
      return;
    }

    // Cleanup VAD and lecture mode
    this.cleanupVAD();
    this.cleanupLectureMode();

    // Stop recording and process final chunk
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Temporarily disable auto-restart
      const wasRecording = this.mediaRecorder.state === 'recording';
      try {
        this.mediaRecorder.stop();
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Error stopping MediaRecorder',
          err
        );
      }

      // Process final chunk if there's data
      if (wasRecording && this.audioChunks.length > 0) {
        this.processChunk();
      }
    }

    // Stop all tracks
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (err) {
          console.warn('[ContinuousConversation] Error stopping track', err);
        }
      });
      this.stream = undefined;
    }

    // Also stop VAD related resources
    if (this.vadSource) {
      try {
        this.vadSource.disconnect();
        this.vadSource = undefined;
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Error disconnecting VAD source',
          err
        );
      }
    }

    if (this.vadAudioContext && this.vadAudioContext.state !== 'closed') {
      try {
        this.vadAudioContext.close();
        this.vadAudioContext = undefined;
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Error closing VAD AudioContext',
          err
        );
      }
    }

    // Cleanup AudioContexts
    this.audioContexts.forEach((ctx) => {
      try {
        ctx.close();
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Error closing AudioContext',
          err
        );
      }
    });
    this.audioContexts = [];

    this.mediaRecorder = undefined;
    this.audioChunks = [];
    this.isRecording$.next(false);

    // Clear retry queue when stopping
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
    }
    this.retryQueue = [];

    // Clear pending transcription requests
    console.log(
      '[ContinuousConversation] Clearing',
      this.pendingRequests,
      'pending transcription requests'
    );
    this.pendingRequests = 0;
    console.log('[ContinuousConversation] 🧹 Cleared retry queue on stop');
  }

  clearTranscripts(): void {
    this.transcripts$.next([]);
    this.chunkIndex = 0; // Reset chunk index when transcripts are cleared
  }

  clearError(): void {
    this.error$.next(null);
  }

  private async processChunk(): Promise<void> {
    if (!this.mediaRecorder || !this.isRecording$.value) {
      return;
    }

    const currentChunks = [...this.audioChunks];
    this.audioChunks = [];
    const currentIndex = this.chunkIndex++;

    if (currentChunks.length === 0) {
      // No data, just restart recording
      if (this.mediaRecorder.state === 'inactive' && this.stream) {
        this.mediaRecorder.start();
      }
      // Reset chunk timing
      this.chunkStartTime = Date.now();
      this.scheduleMaxChunkTimeout();
      return;
    }

    // Create blob from chunks
    const audioBlob = new Blob(currentChunks, {
      type: this.mediaRecorder.mimeType,
    });

    // Industry best practice: Check audio energy before sending
    // Skip chunks that are too small (likely silence) or have low energy
    const shouldSkip = await this.shouldSkipChunk(audioBlob);
    if (shouldSkip) {
      console.log(
        `[ContinuousConversation] Skipping chunk ${currentIndex} - too quiet or too small`
      );
      // Restart recording for next chunk
      if (
        this.isRecording$.value &&
        this.mediaRecorder.state === 'inactive' &&
        this.stream
      ) {
        this.mediaRecorder.start();
        if (this.lectureMode) {
          this.startLectureModeTimer();
        }
      }
      // Reset chunk timing
      this.chunkStartTime = Date.now();
      if (this.lectureMode) {
        this.startLectureModeTimer();
      } else {
        this.scheduleMaxChunkTimeout();
      }
      return;
    }

    // Restart recording for next chunk (if still recording)
    if (
      this.isRecording$.value &&
      this.mediaRecorder.state === 'inactive' &&
      this.stream
    ) {
      try {
        this.mediaRecorder.start();
        if (this.lectureMode) {
          this.startLectureModeTimer();
        }
      } catch (err) {
        console.error(
          '[ContinuousConversation] Failed to restart MediaRecorder',
          err
        );
        this.error$.next('Failed to restart recording');
      }
    }

    // Reset chunk timing for next chunk
    this.chunkStartTime = Date.now();
    if (this.lectureMode) {
      this.startLectureModeTimer();
    } else {
      this.scheduleMaxChunkTimeout();
    }

    // Check if we can send more requests (rate limiting)
    if (this.pendingRequests >= this.MAX_CONCURRENT_REQUESTS) {
      console.log(
        `[ContinuousConversation] Skipping chunk ${currentIndex} - too many pending requests (${this.pendingRequests})`
      );
      return;
    }

    // Emit audio chunk for broadcast feature (if subscribed)
    this.audioChunkEvents$.next({ blob: audioBlob, chunkIndex: currentIndex });

    // Send chunk to backend asynchronously
    this.transcribeChunk(audioBlob, currentIndex).catch((err) => {
      console.error('[ContinuousConversation] Transcription failed', err);
      this.error$.next('Failed to transcribe audio chunk');
    });
  }

  /**
   * Aggressive filtering: Check if audio chunk should be skipped
   * - Too small blob size = likely silence
   * - Low audio energy = likely noise/background/pause
   * - Very short duration = likely incomplete speech
   * - Low zero-crossing rate = likely silence/background noise
   */
  private async shouldSkipChunk(audioBlob: Blob): Promise<boolean> {
    // AGGRESSIVE: Skip if blob is too small (likely silence)
    // Increased thresholds to filter out more silence
    // For 3 seconds at 128kbps, minimum should be ~48KB
    // Now requiring larger chunks to ensure actual speech content
    const isAndroid = /Android/.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // Increased minimum blob sizes - more aggressive filtering
    const MIN_BLOB_SIZE = isIOS ? 30000 : isAndroid ? 25000 : 35000; // iOS: 30KB, Android: 25KB, Web: 35KB
    if (audioBlob.size < MIN_BLOB_SIZE) {
      console.log(
        `[ContinuousConversation] 🚫 Chunk too small (aggressive filter): ${audioBlob.size} bytes < ${MIN_BLOB_SIZE} bytes`
      );
      return true;
    }

    let audioContext: AudioContext | null = null;
    try {
      // Analyze audio energy using Web Audio API
      audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      this.audioContexts.push(audioContext);

      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Check duration - skip very short chunks (likely incomplete)
      const duration = audioBuffer.duration;
      const MIN_DURATION = 0.5; // Require at least 500ms of audio
      if (duration < MIN_DURATION) {
        console.log(
          `[ContinuousConversation] 🚫 Chunk too short (aggressive filter): ${duration.toFixed(
            2
          )}s < ${MIN_DURATION}s`
        );
        return true;
      }

      // Calculate RMS (Root Mean Square) - industry standard for audio energy
      const channelData = audioBuffer.getChannelData(0); // Use first channel
      let sumSquares = 0;
      let peak = 0;
      let zeroCrossings = 0;

      for (let i = 0; i < channelData.length; i++) {
        const sample = channelData[i];
        const abs = Math.abs(sample);

        sumSquares += sample * sample;
        if (abs > peak) {
          peak = abs;
        }

        // Count zero crossings (indicates speech activity)
        if (i > 0) {
          const prevSample = channelData[i - 1];
          if (
            (prevSample >= 0 && sample < 0) ||
            (prevSample < 0 && sample >= 0)
          ) {
            zeroCrossings++;
          }
        }
      }

      const rms = Math.sqrt(sumSquares / channelData.length);

      // Calculate zero-crossing rate (ZCR) - higher indicates more speech-like content
      const zcr = zeroCrossings / channelData.length;

      // AGGRESSIVE thresholds - increased to filter out more silence/pauses
      // Platform-specific thresholds:
      // - iOS: Higher thresholds (more background noise)
      // - Android: Lower thresholds (quieter speech possible)
      // - Web: Higher thresholds (better mics, expect clearer speech)

      // Increased RMS thresholds - require more energy
      const MIN_RMS = isIOS ? 0.025 : isAndroid ? 0.015 : 0.02; // Increased from 0.015/0.008/0.003
      const MIN_PEAK = isIOS ? 0.12 : isAndroid ? 0.08 : 0.1; // Increased from 0.08/0.04/0.05

      // AGGRESSIVE: Higher peak threshold to indicate clear speech
      const HIGH_PEAK_THRESHOLD = isIOS ? 0.15 : isAndroid ? 0.12 : 0.14; // Increased from 0.1/0.1/0.06

      // AGGRESSIVE: Minimum zero-crossing rate (silence has low ZCR)
      // Speech typically has ZCR > 0.01, silence is closer to 0
      const MIN_ZCR = 0.008; // Require some zero crossings (indicates audio activity)

      // Calculate statistics
      const hasHighPeak = peak >= HIGH_PEAK_THRESHOLD;
      const hasLowEnergy = rms < MIN_RMS;
      const hasLowPeak = peak < MIN_PEAK;
      const hasLowZCR = zcr < MIN_ZCR;

      // AGGRESSIVE skip logic - filter if ANY suspicious condition:
      // 1. Always skip if peak is very low (definitely silence)
      // 2. Skip if RMS is low AND peak isn't high enough to indicate clear speech
      // 3. Skip if zero-crossing rate is too low (indicates silence/noise)
      // 4. Skip if peak is moderately low AND RMS is low (likely pause/silence)
      const shouldSkip =
        hasLowPeak || // Very low peak = silence
        (hasLowEnergy && !hasHighPeak) || // Low energy and no strong peak = silence/pause
        hasLowZCR || // Low zero-crossing rate = silence/noise
        (peak < HIGH_PEAK_THRESHOLD && rms < MIN_RMS * 1.5); // Moderate peak with low RMS = likely pause

      if (shouldSkip) {
        console.log(
          `[ContinuousConversation] 🚫 Low energy/silence detected (aggressive filter): RMS: ${rms.toFixed(
            4
          )} (min: ${MIN_RMS}), Peak: ${peak.toFixed(
            4
          )} (min: ${MIN_PEAK}, high: ${HIGH_PEAK_THRESHOLD}), ZCR: ${zcr.toFixed(
            4
          )} (min: ${MIN_ZCR}), Duration: ${duration.toFixed(2)}s`
        );
      } else {
        console.log(
          `[ContinuousConversation] ✅ Chunk passed aggressive filter: RMS: ${rms.toFixed(
            4
          )}, Peak: ${peak.toFixed(4)}, ZCR: ${zcr.toFixed(
            4
          )}, Duration: ${duration.toFixed(2)}s`
        );
      }

      return shouldSkip;
    } catch (err) {
      console.warn(
        '[ContinuousConversation] Could not analyze audio energy',
        err
      );
      // If we can't analyze, be conservative and skip (aggressive approach)
      return true;
    } finally {
      // Cleanup AudioContext
      if (audioContext) {
        try {
          const index = this.audioContexts.indexOf(audioContext);
          if (index > -1) {
            this.audioContexts.splice(index, 1);
          }
          audioContext.close();
        } catch (err) {
          console.warn(
            '[ContinuousConversation] Error closing AudioContext',
            err
          );
        }
      }
    }
  }

  /**
   * Filter hallucinations like "Please speak in [language]" for all supported languages
   */
  private isHallucination(text: string, sourceLanguage?: string): boolean {
    const normalizedText = text.toLowerCase().trim();

    // Language-specific hallucination patterns
    const hallucinationPatterns: { [key: string]: RegExp[] } = {
      // English
      en: [
        /^please\s+speak\s+in\s+english$/i,
        /^please\s+speak\s+english$/i,
        /^speak\s+in\s+english$/i,
        /^speak\s+english$/i,
        /^please\s+speak\s+in\s+english\s+only$/i,
        /^speak\s+in\s+english\s+only$/i,
        /^please\s+use\s+english$/i,
        /^use\s+english$/i,
        /^please\s+speak\s+in\s+[a-zA-Z]+$/i, // Catches "Please speak in Korean" (without "only")
        /^Hello,?\s+thank\s+you,?\s+yes,?\s+no\.?$/i, // Common words during silence: "Hello, thank you, yes, no."
        /^Hello\.?\s*thank\s+you\.?\s*yes\.?\s*no\.?$/i, // Variant with periods
        /^Hello,?\s+thank\s+you,?\s+yes,?\s+no$/i, // Without final period
      ],
      // Korean
      ko: [
        /^영어로\s+말해\s+주세요$/,
        /^영어로\s+말하세요$/,
        /^영어로만\s+말해\s+주세요$/,
        /^영어로만\s+말하세요$/,
        /^영어를\s+사용해\s+주세요$/,
        /^영어를\s+사용하세요$/,
        /^한국어로\s+말해\s+주세요$/,
        /^한국어로\s+말하세요$/,
        /^한국어로만\s+말해\s+주세요$/,
        /^안녕하세요\.?$/i, // Korean greeting alone (hallucination during silence)
        /^안녕하세요$/i, // Korean greeting without period
      ],
      // Chinese (Mandarin)
      'zh-CN': [
        /^请说英语$/,
        /^请用英语$/,
        /^请用英语说$/,
        /^请只使用英语$/,
        /^请说中文$/,
        /^请用中文$/,
        /^请用中文说$/,
      ],
      // Chinese (Cantonese)
      'zh-HK': [
        /^請講英語$/,
        /^請用英語$/,
        /^請用英語講$/,
        /^請只使用英語$/,
        /^請講中文$/,
        /^請用中文$/,
        /^請用中文講$/,
      ],
      // Kyrgyz
      ky: [
        /^англисче\s+сүйлө\s+бериңиз$/i,
        /^англисче\s+сүйлөңүз$/i,
        /^англисче\s+гана\s+сүйлө\s+бериңиз$/i,
        /^кыргызча\s+сүйлө\s+бериңиз$/i,
        /^кыргызча\s+сүйлөңүз$/i,
      ],
      // Russian
      ru: [
        /^пожалуйста\s+говорите\s+по-английски$/i,
        /^говорите\s+по-английски$/i,
        /^говорите\s+только\s+по-английски$/i,
        /^пожалуйста\s+говорите\s+по-русски$/i,
        /^говорите\s+по-русски$/i,
        /^говорите\s+только\s+по-русски$/i,
      ],
      // Kazakh
      kk: [
        /^ағылшынша\s+айтыңыз$/i,
        /^ағылшынша\s+айтуыңызды\s+сұраймыз$/i,
        /^ағылшынша\s+ғана\s+айтыңыз$/i,
        /^қазақша\s+айтыңыз$/i,
        /^қазақша\s+айтуыңызды\s+сұраймыз$/i,
      ],
      // Tajik
      tg: [
        /^лутфан\s+ба\s+англисӣ\s+сухан\s+гӯед$/i,
        /^ба\s+англисӣ\s+сухан\s+гӯед$/i,
        /^танҳо\s+ба\s+англисӣ\s+сухан\s+гӯед$/i,
        /^лутфан\s+ба\s+тоҷикӣ\s+сухан\s+гӯед$/i,
        /^ба\s+тоҷикӣ\s+сухан\s+гӯед$/i,
      ],
      // Turkmen
      tk: [
        /^haýyş\s+iňlisçe\s+gürleň$/i,
        /^iňlisçe\s+gürleň$/i,
        /^diňe\s+iňlisçe\s+gürleň$/i,
        /^haýyş\s+türkmençe\s+gürleň$/i,
        /^türkmençe\s+gürleň$/i,
      ],
      // Uzbek
      uz: [
        /^iltimos\s+ingliz\s+tilida\s+gapiring$/i,
        /^ingliz\s+tilida\s+gapiring$/i,
        /^faqat\s+ingliz\s+tilida\s+gapiring$/i,
        /^iltimos\s+o'zbek\s+tilida\s+gapiring$/i,
        /^o'zbek\s+tilida\s+gapiring$/i,
      ],
      // Farsi/Persian
      fa: [
        /^لطفاً\s+به\s+انگلیسی\s+صحبت\s+کنید$/,
        /^به\s+انگلیسی\s+صحبت\s+کنید$/,
        /^فقط\s+به\s+انگلیسی\s+صحبت\s+کنید$/,
        /^لطفاً\s+به\s+فارسی\s+صحبت\s+کنید$/,
        /^به\s+فارسی\s+صحبت\s+کنید$/,
      ],
      // Spanish
      es: [
        /^por\s+favor\s+habla\s+en\s+inglés$/i,
        /^habla\s+en\s+inglés$/i,
        /^por\s+favor\s+habla\s+inglés$/i,
        /^habla\s+inglés$/i,
        /^por\s+favor\s+habla\s+solo\s+en\s+inglés$/i,
        /^habla\s+solo\s+en\s+inglés$/i,
        /^por\s+favor\s+usa\s+inglés$/i,
        /^usa\s+inglés$/i,
        /^por\s+favor\s+habla\s+en\s+español$/i,
        /^habla\s+en\s+español$/i,
        /^por\s+favor\s+habla\s+español$/i,
        /^habla\s+español$/i,
        /^por\s+favor\s+habla\s+solo\s+en\s+español$/i,
        /^habla\s+solo\s+en\s+español$/i,
        /^por\s+favor\s+usa\s+español$/i,
        /^usa\s+español$/i,
      ],
    };

    // Check patterns for the source language
    if (sourceLanguage) {
      const lang = sourceLanguage.toLowerCase();
      const patterns = hallucinationPatterns[lang] || [];
      for (const pattern of patterns) {
        if (pattern.test(normalizedText)) {
          console.log(
            `[ContinuousConversation] 🚫 Hallucination detected: "${text}" (matched pattern for ${sourceLanguage})`
          );
          return true;
        }
      }
    }

    // Also check all languages (as translation might be in target language)
    for (const lang of Object.keys(hallucinationPatterns)) {
      const patterns = hallucinationPatterns[lang];
      for (const pattern of patterns) {
        if (pattern.test(normalizedText)) {
          console.log(
            `[ContinuousConversation] 🚫 Hallucination detected: "${text}" (matched pattern for ${lang})`
          );
          return true;
        }
      }
    }

    // General patterns that apply to all languages
    const generalPatterns = [
      /^please\s+speak\s+in\s+[a-zA-Z]+$/i, // Catches "Please speak in Korean", "Please speak in English", etc.
      /^speak\s+in\s+[a-zA-Z]+$/i, // Catches "Speak in Korean", "Speak in English", etc.
      /^Hello,?\s+thank\s+you,?\s+yes,?\s+no\.?$/i, // "Hello, thank you, yes, no."
      /^Hello\.?\s*thank\s+you\.?\s*yes\.?\s*no\.?$/i, // Variant with periods
      /^Hello,?\s+thank\s+you,?\s+yes,?\s+no$/i, // Without final period
    ];

    for (const pattern of generalPatterns) {
      if (pattern.test(normalizedText)) {
        console.log(
          `[ContinuousConversation] 🚫 Hallucination detected: "${text}" (matched general pattern)`
        );
        return true;
      }
    }

    // Filter out conversation topic words that are hallucinated back
    if (this.systemPrompt && this.systemPrompt.trim()) {
      const topicWords = this.systemPrompt
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 2); // Only consider words longer than 2 characters

      // Check if transcript text matches or contains topic words as standalone text
      // This filters cases where the topic words are hallucinated back (e.g., "dental lecture" -> "dental" or "lecture")
      if (topicWords.length > 0) {
        // Check if transcript is exactly one of the topic words
        if (topicWords.includes(normalizedText)) {
          console.log(
            `[ContinuousConversation] 🚫 Filtered topic word hallucination: "${text}" (matches conversation topic word)`
          );
          return true;
        }

        // Check if transcript contains only topic words (e.g., "dental lecture" when topic is "dental lecture")
        const transcriptWords = normalizedText.split(/\s+/).filter((w) => w.length > 0);
        if (
          transcriptWords.length > 0 &&
          transcriptWords.every((word) => topicWords.includes(word)) &&
          transcriptWords.length <= topicWords.length
        ) {
          console.log(
            `[ContinuousConversation] 🚫 Filtered topic words hallucination: "${text}" (contains only conversation topic words)`
          );
          return true;
        }

        // Check if transcript starts with topic words (e.g., "dental lecture is" when topic is "dental lecture")
        // But only if it's a very short phrase (likely hallucination)
        if (transcriptWords.length <= 3) {
          const startsWithTopic = topicWords.some((topicWord) =>
            normalizedText.startsWith(topicWord)
          );
          if (startsWithTopic && transcriptWords.length <= topicWords.length + 1) {
            console.log(
              `[ContinuousConversation] 🚫 Filtered topic word at start: "${text}" (starts with conversation topic word)`
            );
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Check if a transcript is a duplicate of a recently processed transcript
   * Filters out repeated messages that occur during silence
   */
  private isDuplicate(text: string): boolean {
    const normalizedText = text.toLowerCase().trim();
    const now = Date.now();
    const lastSeen = this.recentTranscripts.get(normalizedText);

    if (lastSeen && now - lastSeen < this.DUPLICATE_WINDOW_MS) {
      return true; // This text was seen recently (within the time window)
    }

    return false;
  }

  /**
   * Clean up old entries from the duplicate tracking map to prevent memory leaks
   */
  private cleanupOldDuplicates(): void {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    this.recentTranscripts.forEach((timestamp, text) => {
      if (now - timestamp > this.DUPLICATE_WINDOW_MS) {
        entriesToDelete.push(text);
      }
    });

    entriesToDelete.forEach((text) => {
      this.recentTranscripts.delete(text);
    });
  }

  private async transcribeChunk(
    audioBlob: Blob,
    chunkIndex: number
  ): Promise<void> {
    this.pendingRequests++;

    try {
      // Add timeout to prevent hanging requests (15 seconds max)
      const timeoutMs = 15000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Transcription timeout')), timeoutMs);
      });

      // Use TranslatorService to transcribe
      // translateToEnglish should be true if targetLanguage is 'en', false otherwise
      // But we also pass targetLanguage explicitly to support any target language
      const translateToEnglish = this.targetLanguage === 'en';

      // Collect conversation context from past chunks (last 5 chunks' original text)
      // This helps improve translation quality by understanding the conversation flow
      const MAX_CONTEXT_CHUNKS = 5;
      const currentTranscripts = this.transcripts$.value;
      const conversationContext = currentTranscripts
        .slice(-MAX_CONTEXT_CHUNKS) // Get last 5 chunks
        .map((t) => t.originalText || t.text) // Use originalText if available, otherwise use text
        .filter((text) => text && text.trim().length > 0); // Filter empty texts

      console.log(
        '[ContinuousConversation] 📚 Collecting conversation context:',
        {
          totalChunks: currentTranscripts.length,
          contextChunks: conversationContext.length,
          contextPreview: conversationContext.map((t) => t.substring(0, 30)),
        }
      );

      const response = await Promise.race([
        this.translatorService.transcribe(
          audioBlob,
          translateToEnglish,
          this.sourceLanguage, // language (backward compat)
          this.systemPrompt, // Pass the stored system prompt/topic
          false, // useDiarization
          this.targetLanguage, // targetLanguage - for translation to any language
          this.sourceLanguage, // sourceLanguage - explicit source language
          conversationContext, // Pass conversation context for better translation
          this.useAdvancedModel
        ),
        timeoutPromise,
      ]);

      if (response && response.text && response.text.trim()) {
        const transcriptText = response.text.trim();

        // Filter hallucinations like "Please speak in [language]"
        if (this.isHallucination(transcriptText, this.sourceLanguage)) {
          console.log(
            `[ContinuousConversation] 🚫 Filtered hallucination: "${transcriptText}"`
          );
          return; // Skip adding this transcript
        }

        // Filter duplicate messages within a time window (prevents repeated messages during silence)
        if (this.isDuplicate(transcriptText)) {
          console.log(
            `[ContinuousConversation] 🚫 Filtered duplicate: "${transcriptText}"`
          );
          return; // Skip adding this duplicate transcript
        }

        // Track this transcript to prevent duplicates
        const normalizedText = transcriptText.toLowerCase().trim();
        this.recentTranscripts.set(normalizedText, Date.now());

        // Clean up old entries from the duplicate tracking map
        this.cleanupOldDuplicates();

        // Add to transcripts list
        const transcripts = this.transcripts$.value;

        // Save to conversation history
        // Backend now returns both originalText and translatedText when translation was performed
        const needsTranslation =
          this.targetLanguage && this.targetLanguage !== this.sourceLanguage;

        // Backend returns:
        // - When translation is performed: originalText (source language), translatedText (target language), text (translated)
        // - When no translation: originalText is undefined, translatedText is undefined, text (original)
        const responseOriginalText = response.originalText;
        const responseTranslatedText = response.translatedText;

        // Determine original text for storing in transcript (for future context)
        const originalTextForContext =
          responseOriginalText ?? (needsTranslation ? '' : transcriptText);

        transcripts.push({
          text: transcriptText,
          timestamp: new Date(),
          chunkIndex,
          originalText: originalTextForContext, // Store original text for future context
        });

        // Limit transcript history to prevent memory issues
        if (transcripts.length > this.MAX_TRANSCRIPTS) {
          transcripts.shift(); // Remove oldest
        }

        this.transcripts$.next([...transcripts]);

        // Determine original and translated text:
        // If backend returned originalText, use it (translation was performed)
        // Otherwise, use transcriptText as original (no translation was performed)
        // The transcriptText is the translated text when translation was performed, or original when not
        let originalText =
          responseOriginalText ?? (needsTranslation ? '' : transcriptText);
        let translatedText =
          responseTranslatedText ?? (needsTranslation ? transcriptText : '');

        // Additional safety check: Filter hallucinations from original and translated text
        // (in case they somehow passed through the first filter)
        if (
          originalText &&
          this.isHallucination(originalText, this.sourceLanguage)
        ) {
          console.log(
            `[ContinuousConversation] 🚫 Filtered hallucination from originalText: "${originalText}"`
          );
          originalText = ''; // Skip saving hallucination
        }

        if (
          translatedText &&
          this.isHallucination(translatedText, this.targetLanguage)
        ) {
          console.log(
            `[ContinuousConversation] 🚫 Filtered hallucination from translatedText: "${translatedText}"`
          );
          translatedText = ''; // Skip saving hallucination
        }

        // Skip saving to history if both original and translated text are empty or hallucinations
        if (!originalText && !translatedText && !transcriptText) {
          console.log(
            '[ContinuousConversation] 🚫 Skipping history save - all texts are empty or filtered'
          );
          return;
        }

        // Debug logging
        console.log('[ContinuousConversation] Saving to history:', {
          needsTranslation,
          hasOriginalText: !!responseOriginalText,
          hasTranslatedText: !!responseTranslatedText,
          originalText,
          translatedText,
          transcriptText,
          responseText: response.text,
          sourceLanguage: this.sourceLanguage,
          targetLanguage: this.targetLanguage,
        });

        // Ensure sourceLanguage is set before saving (should always be set in startRecording, but add safeguard)
        const sourceLang = this.sourceLanguage || 'en';
        const targetLang = this.targetLanguage || 'en';

        console.log(
          '[ContinuousConversation] Saving to history with languages:',
          {
            sourceLanguage: sourceLang,
            targetLanguage: targetLang,
            originalTextLength: (originalText || transcriptText).length,
            translatedTextLength: translatedText.length,
          }
        );

        this.conversationHistoryService.addConversation({
          originalText: originalText || transcriptText, // Fallback to transcriptText if originalText is empty
          translatedText: translatedText || '', // Use translated text if available
          userLanguage: sourceLang, // Use the source language that was set when recording started
          targetLanguage: targetLang,
          confidence: response.confidence || 0,
          inputType: 'speech',
          // Note: We don't have audio URL/blob for each chunk in continuous mode
          // If needed, you could store the audioBlob for each chunk
        });
      }
    } catch (err) {
      console.error(
        '[ContinuousConversation] 💥 Transcription error caught:',
        err
      );
      console.error('[ContinuousConversation] 💥 Error type:', typeof err);
      console.error(
        '[ContinuousConversation] 💥 Error instanceof Error:',
        err instanceof Error
      );

      if (err instanceof Error) {
        console.error(
          '[ContinuousConversation] 💥 Error message:',
          err.message
        );
        console.error('[ContinuousConversation] 💥 Error stack:', err.stack);
        console.error('[ContinuousConversation] 💥 Error name:', err.name);
      } else {
        console.error(
          '[ContinuousConversation] 💥 Non-Error object:',
          JSON.stringify(err, null, 2)
        );
      }

      // Check for word limit errors - show message like manual recording
      // The error message from translator service is already translated to UI language
      if (err instanceof Error) {
        const errorMessage = err.message || '';

        // Language-agnostic detection: Check for word limit error indicators in multiple languages
        // Also check if error contains word count patterns (e.g., "165 out of 170")
        const isWordLimitError =
          errorMessage.includes('Word limit exceeded') ||
          errorMessage.includes('Please buy more words') ||
          errorMessage.includes('word limit') ||
          errorMessage.includes('单词限制') || // Chinese
          errorMessage.includes('已使用') || // Chinese "used"
          errorMessage.includes('단어 제한') || // Korean
          errorMessage.includes('사용했습니다') || // Korean "used"
          errorMessage.includes('превышен лимит') || // Russian
          errorMessage.includes('использовано') || // Russian "used"
          errorMessage.includes('сөздүн лимити') || // Kyrgyz
          errorMessage.includes('колдонулду') || // Kyrgyz "used"
          /已使用\s*\d+\s*\/\s*\d+/.test(errorMessage) || // Chinese pattern "已使用 X / Y"
          /已使用\s*\d+\s*，共\s*\d+/.test(errorMessage) || // Chinese pattern "已使用 X，共 Y"
          /使用\s*\d+\s*\/\s*\d+/.test(errorMessage) || // Chinese pattern "使用 X / Y"
          /\d+\s*out\s*of\s*\d+/.test(errorMessage) || // English pattern "X out of Y"
          /\d+\s*\/\s*\d+\s*words/.test(errorMessage) || // Pattern "X / Y words"
          /\d+\s*중\s*\d+/.test(errorMessage) || // Korean pattern "X 중 Y"
          /\d+\s*из\s*\d+/.test(errorMessage); // Russian pattern "X из Y"

        if (isWordLimitError) {
          console.log(
            '[ContinuousConversation] 💬 Word limit error detected:',
            errorMessage
          );

          // Error message from translator service is already translated to UI language
          // Just emit it directly to display in modal
          this.error$.next(errorMessage);

          // Stop recording automatically when word limit is reached (like manual recording)
          if (this.isRecording$.value) {
            console.log(
              '[ContinuousConversation] 🛑 Stopping recording due to word limit'
            );
            this.stopRecording();
          }
          // Don't continue recording - word limit is a hard stop
          return;
        }
      }

      // Handle timeout errors - add to retry queue
      if (err instanceof Error && err.message === 'Transcription timeout') {
        console.log(
          '[ContinuousConversation] ⏱️ Timeout error - checking retry queue'
        );

        // Check if this is a retry attempt (chunkIndex exists in retry queue with retryCount > 0)
        const existingRetry = this.retryQueue.find(
          (r) => r.chunkIndex === chunkIndex
        );

        if (existingRetry && existingRetry.retryCount > 0) {
          // This is a retry that timed out - retry count was already incremented in processRetryQueue
          console.log(
            `[ContinuousConversation] ⚠️ Retry ${existingRetry.retryCount}/${this.MAX_RETRIES} failed for chunk ${chunkIndex}`
          );

          if (existingRetry.retryCount >= this.MAX_RETRIES) {
            // Max retries exceeded - remove from queue and log error (don't display to user)
            const index = this.retryQueue.indexOf(existingRetry);
            if (index > -1) {
              this.retryQueue.splice(index, 1);
            }
            const failedMessage = this.translationService.getTranslation(
              'transcriptionFailed'
            );
            console.error(
              `[ContinuousConversation] ❌ Chunk ${chunkIndex} exceeded max retries - logging only (not displaying to user):`,
              {
                message: failedMessage,
                chunkIndex,
                retryCount: existingRetry.retryCount,
              }
            );
          } else {
            // Schedule another retry with exponential backoff
            const backoffDelay =
              this.RETRY_DELAY_MS * Math.pow(2, existingRetry.retryCount);
            setTimeout(() => {
              this.processRetryQueue();
            }, backoffDelay);
          }
        } else {
          // First timeout - add to retry queue
          this.retryQueue.push({
            audioBlob,
            chunkIndex,
            retryCount: 0,
            timestamp: Date.now(),
          });
          console.log(
            `[ContinuousConversation] 📦 Added chunk ${chunkIndex} to retry queue (queue size: ${this.retryQueue.length})`
          );

          // Log timeout error but don't display to user
          const timeoutMessage = this.translationService.getTranslation(
            'transcriptionTimeoutMessage'
          );
          console.error(
            '[ContinuousConversation] ⏱️ Transcription timeout - logging only (not displaying to user):',
            {
              message: timeoutMessage,
              chunkIndex,
              audioBlobSize: audioBlob.size,
              sourceLanguage: this.sourceLanguage,
              targetLanguage: this.targetLanguage,
            }
          );

          // Process retry queue after a delay
          this.scheduleRetry();
        }
      } else if (err instanceof Error) {
        // Log detailed error information for debugging (but don't display to user)
        console.error(
          '[ContinuousConversation] 💥 Transcription error details (logging only, not displaying to user):',
          {
            message: err.message,
            stack: err.stack,
            name: err.name,
            chunkIndex,
            audioBlobSize: audioBlob.size,
            sourceLanguage: this.sourceLanguage,
            targetLanguage: this.targetLanguage,
          }
        );

        // Don't show error to user - just log it
        // Continue recording silently
      } else {
        // Non-Error objects - log but don't display
        console.error(
          '[ContinuousConversation] 💥 Transcription error (non-Error) - logging only:',
          err
        );
        // Don't show error to user - just log it
      }
    } finally {
      this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    }
  }

  /**
   * Schedule retry of failed chunks
   */
  private scheduleRetry(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    // Process retry queue after initial delay
    this.retryTimeout = setTimeout(() => {
      this.processRetryQueue();
    }, this.RETRY_DELAY_MS);
  }

  /**
   * Process retry queue - retry failed chunks
   */
  private async processRetryQueue(): Promise<void> {
    if (this.retryQueue.length === 0) {
      return;
    }

    console.log(
      `[ContinuousConversation] 🔄 Processing retry queue (${this.retryQueue.length} chunks)`
    );

    // Process retries one at a time to avoid overwhelming the API
    // Find the oldest item (lowest timestamp)
    const retryItem = this.retryQueue.reduce((oldest, current) =>
      current.timestamp < oldest.timestamp ? current : oldest
    );

    if (!retryItem) {
      return;
    }

    // Check if we've exceeded max retries
    if (retryItem.retryCount >= this.MAX_RETRIES) {
      console.log(
        `[ContinuousConversation] ❌ Chunk ${retryItem.chunkIndex} exceeded max retries, giving up`
      );
      // Remove from queue
      const index = this.retryQueue.indexOf(retryItem);
      if (index > -1) {
        this.retryQueue.splice(index, 1);
      }
      // Show error message
      const failedMessage = this.translationService.getTranslation(
        'transcriptionFailed'
      );
      this.error$.next(failedMessage);
      // Process next item in queue
      if (this.retryQueue.length > 0) {
        this.scheduleRetry();
      }
      return;
    }

    // Increment retry count BEFORE retrying
    retryItem.retryCount++;
    console.log(
      `[ContinuousConversation] 🔄 Retrying chunk ${retryItem.chunkIndex} (attempt ${retryItem.retryCount}/${this.MAX_RETRIES})`
    );

    // Show retry message
    const retryMessage = this.translationService.getTranslation(
      'retryingTranscription'
    );
    this.error$.next(retryMessage);

    // Retry transcription - if it times out, the timeout handler will update retryItem
    try {
      await this.transcribeChunk(retryItem.audioBlob, retryItem.chunkIndex);
      console.log(
        `[ContinuousConversation] ✅ Retry successful for chunk ${retryItem.chunkIndex}`
      );
      // Remove from queue on success
      const index = this.retryQueue.indexOf(retryItem);
      if (index > -1) {
        this.retryQueue.splice(index, 1);
      }
      // Clear error if retry was successful
      this.error$.next(null);
    } catch (err) {
      // Error is handled in transcribeChunk - it will update retryItem if it's a timeout
      // If it's not a timeout, we'll handle it here
      if (!(err instanceof Error && err.message === 'Transcription timeout')) {
        console.error(
          `[ContinuousConversation] ❌ Retry failed for chunk ${retryItem.chunkIndex}:`,
          err
        );
        // Remove from queue on non-timeout error
        const index = this.retryQueue.indexOf(retryItem);
        if (index > -1) {
          this.retryQueue.splice(index, 1);
        }
        const failedMessage = this.translationService.getTranslation(
          'transcriptionFailed'
        );
        this.error$.next(failedMessage);
      }
      // If it's a timeout, the timeout handler in transcribeChunk will handle it
    }

    // Process next item in queue if any
    if (this.retryQueue.length > 0) {
      this.scheduleRetry();
    }
  }

  /**
   * Initialize Voice Activity Detection (VAD) for pause-based chunking
   */
  private initializeVAD(): void {
    if (!this.stream) {
      return;
    }

    try {
      this.vadAudioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      this.vadAnalyser = this.vadAudioContext.createAnalyser();
      this.vadAnalyser.fftSize = 2048;
      this.vadAnalyser.smoothingTimeConstant = 0.8;

      this.vadSource = this.vadAudioContext.createMediaStreamSource(
        this.stream
      );
      this.vadSource.connect(this.vadAnalyser);

      // Start checking for pauses
      this.lastSpeechTime = Date.now();
      this.isInSpeech = false;

      this.vadCheckInterval = setInterval(() => {
        this.checkForPause();
      }, this.VAD_INTERVAL_MS);

      console.log(
        '[ContinuousConversation] VAD initialized for pause-based chunking'
      );
    } catch (err) {
      console.error('[ContinuousConversation] Failed to initialize VAD', err);
      // Fallback to time-based chunking if VAD fails
    }
  }

  /**
   * Check audio energy and detect pauses
   */
  private checkForPause(): void {
    if (!this.vadAnalyser || !this.isRecording$.value) {
      return;
    }

    try {
      const bufferLength = this.vadAnalyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      this.vadAnalyser.getByteTimeDomainData(dataArray);

      // Calculate RMS (Root Mean Square) for audio energy
      let sumSquares = 0;
      let peak = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128; // Normalize to -1 to 1
        sumSquares += normalized * normalized;
        const abs = Math.abs(normalized);
        if (abs > peak) {
          peak = abs;
        }
      }
      const rms = Math.sqrt(sumSquares / bufferLength);

      // Platform-specific thresholds
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const minRMS = isIOS ? 0.015 : isAndroid ? 0.008 : this.VAD_RMS_THRESHOLD;
      const minPeak = isIOS ? 0.08 : isAndroid ? 0.04 : this.VAD_PEAK_THRESHOLD;

      const hasSpeech = rms >= minRMS && peak >= minPeak;
      const now = Date.now();

      if (hasSpeech) {
        this.lastSpeechTime = now;
        if (!this.isInSpeech) {
          this.isInSpeech = true;
          console.log('[ContinuousConversation] Speech detected');
        }
      } else {
        // Check if we've had enough silence to trigger a pause
        const silenceDuration = now - this.lastSpeechTime;
        const chunkDuration = now - this.chunkStartTime;

        if (this.isInSpeech && silenceDuration >= this.PAUSE_DURATION_MS) {
          // Pause detected - create chunk if we have minimum duration
          if (chunkDuration >= this.MIN_CHUNK_DURATION_MS) {
            console.log(
              `[ContinuousConversation] Pause detected (${silenceDuration}ms silence), creating chunk`
            );
            this.isInSpeech = false;
            this.createChunkOnPause();
          }
        }
      }
    } catch (err) {
      console.warn('[ContinuousConversation] VAD check error', err);
    }
  }

  /**
   * Lecture mode: fixed-interval chunking every 20 seconds
   */
  private startLectureModeTimer(): void {
    this.cleanupLectureMode();
    if (!this.lectureMode) {
      return;
    }

    this.lectureModeTimer = setTimeout(() => {
      if (!this.isRecording$.value || !this.mediaRecorder || !this.stream) {
        return;
      }
      try {
        this.mediaRecorder.stop(); // onstop will process chunk and restart
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Lecture mode: error stopping recorder for interval chunk',
          err
        );
      }
    }, this.LECTURE_MODE_INTERVAL_MS);
  }

  private cleanupLectureMode(): void {
    if (this.lectureModeTimer) {
      clearTimeout(this.lectureModeTimer);
      this.lectureModeTimer = undefined;
    }
  }

  /**
   * Create chunk when pause is detected
   */
  private createChunkOnPause(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
      return;
    }

    // Stop recording to trigger onstop, which will process the chunk
    try {
      this.mediaRecorder.stop();
    } catch (err) {
      console.error(
        '[ContinuousConversation] Error stopping MediaRecorder on pause',
        err
      );
    }
  }

  /**
   * Schedule timeout to force chunk creation at max duration
   */
  private scheduleMaxChunkTimeout(): void {
    if (this.maxChunkTimeout) {
      clearTimeout(this.maxChunkTimeout);
    }

    this.maxChunkTimeout = setTimeout(() => {
      if (
        this.isRecording$.value &&
        this.mediaRecorder &&
        this.mediaRecorder.state === 'recording'
      ) {
        console.log(
          '[ContinuousConversation] Max chunk duration reached, forcing chunk creation'
        );
        try {
          this.mediaRecorder.stop();
        } catch (err) {
          console.error(
            '[ContinuousConversation] Error stopping MediaRecorder on max timeout',
            err
          );
        }
      }
    }, this.MAX_CHUNK_DURATION_MS);
  }

  /**
   * Cleanup VAD resources
   */
  private cleanupVAD(): void {
    if (this.vadCheckInterval) {
      clearInterval(this.vadCheckInterval);
      this.vadCheckInterval = undefined;
    }

    if (this.maxChunkTimeout) {
      clearTimeout(this.maxChunkTimeout);
      this.maxChunkTimeout = undefined;
    }

    if (this.vadSource) {
      try {
        this.vadSource.disconnect();
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Error disconnecting VAD source',
          err
        );
      }
      this.vadSource = undefined;
    }

    if (this.vadAudioContext) {
      try {
        this.vadAudioContext.close();
      } catch (err) {
        console.warn(
          '[ContinuousConversation] Error closing VAD AudioContext',
          err
        );
      }
      this.vadAudioContext = undefined;
    }

    this.vadAnalyser = undefined;
  }
}
